import { IconCollection } from "components/Icon/IconCollection";

/**
 * Pure resolve/reopen logic for an ASSIGNED record comment
 * (operator-task-fe-comment-assignment-make-useful).
 *
 * An assigned comment is a lightweight to-do for its assignee. The assignee
 * checks it off with a checkmark next to the "Assigned to" badge (Margot,
 * 2026-09-29). Resolving sets `resolved_at`; the backend stamps `resolved_by`
 * and lets the assignee do this even without write access to the record, but
 * not on a read-only record.
 */

/** The minimal comment shape the resolve logic needs. */
export interface ResolvableComment {
    author_identity_id: string | null;
    assignee_identity_id: string | null;
    resolved_at: string | null;
}

export interface ResolveToggleAction {
    icon: (typeof IconCollection)[keyof typeof IconCollection];
    title: string;
    ariaLabel: string;
    /** The `resolved_at` value to write: a timestamp to resolve, null to reopen. */
    nextResolvedAt: string | null;
}

/**
 * The action the resolve toggle performs for a comment in its current state.
 * `now` is injectable for tests.
 */
export const resolveToggleAction = (
    resolvedAt: string | null,
    now: () => Date = () => new Date(),
): ResolveToggleAction =>
    resolvedAt
        ? {
              icon: IconCollection.undo,
              title: "Reopen",
              ariaLabel: "Reopen",
              nextResolvedAt: null,
          }
        : {
              icon: IconCollection.check,
              title: "Mark as done",
              ariaLabel: "Mark as done",
              nextResolvedAt: now().toISOString(),
          };

/** The read-only flags of a comment's record. */
export interface ReadOnlyFlags {
    is_readonly?: boolean | null;
    is_project_readonly?: boolean | null;
    is_bundle_readonly?: boolean | null;
}

/**
 * Whether the record, or its project or bundle, is read-only. The backend
 * refuses every comment change on such a record (posting, editing, assigning,
 * resolving, deleting), so the UI hides those actions.
 */
export const isRecordReadOnly = (record: ReadOnlyFlags | null | undefined): boolean =>
    Boolean(record?.is_readonly || record?.is_project_readonly || record?.is_bundle_readonly);

/**
 * Whether the viewer may resolve/reopen the comment. Mirrors the backend rule:
 * the assignee always may; otherwise the author or anyone with write access to
 * the record (`canWrite`). Unassigned comments have no toggle — resolving is
 * the assignee's to-do action. Comments on a read-only record are not
 * resolvable (`isReadOnly`).
 */
export const canToggleResolve = (
    comment: ResolvableComment,
    currentIdentityId: string | undefined,
    canWrite: boolean,
    isReadOnly = false,
): boolean => {
    if (isReadOnly || !comment.assignee_identity_id) {
        return false;
    }
    if (currentIdentityId && comment.assignee_identity_id === currentIdentityId) {
        return true;
    }
    if (currentIdentityId && comment.author_identity_id === currentIdentityId) {
        return true;
    }
    return canWrite;
};
